import type { TFunction } from 'i18next';

import i18n from '../locales/i18n';
import { ActionDownloadSelection, ActionRunCommandHints, ActionRunSummary, ActionTargetRun } from '../types/actionTypes';
import { formatActionPhaseLabel } from './actionProgress';

const ACTION_TYPE_LABEL_KEYS: Record<string, string> = {
  REBOOT: 'rtuActions.items.reboot',
  UPDATE_CONFIG: 'rtuActions.items.updateConfig',
  UPDATE_DEPLOY: 'rtuActions.items.updateDeployment',
  UPDATE_LICENSE: 'rtuActions.items.uploadLicense',
  RTU_INFO: 'rtuActions.items.rtuInfo',
  RTU_DEBUG_LOG: 'rtuActions.items.downloadDebugLog',
  DOWNLOAD_CONFIG: 'rtuActions.items.downloadConfig',
  DOWNLOAD_DEPLOY: 'rtuActions.items.downloadDeployment',
};

const normalizeToken = (value?: string): string =>
  String(value ?? '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toUpperCase();

const translate = (t: TFunction | undefined, key: string, defaultValue: string): string =>
  String(t ? t(key, { defaultValue }) : i18n.t(key, { defaultValue }));

export const humanizeActionToken = (value?: string): string => {
  if (!value) return 'Action';
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

export const actionTypeDisplayName = (value: string | undefined, t: TFunction): string => {
  const normalized = normalizeToken(value);
  if (!normalized) return t('actionsWorkbench.genericAction', { defaultValue: 'Action' });
  return t(ACTION_TYPE_LABEL_KEYS[normalized] || `audit.actions.actionTypeOptions.${normalized}`, {
    defaultValue: humanizeActionToken(value),
  });
};

export const actionRunTitle = (run: Pick<ActionRunSummary, 'id' | 'actionType'>, t: TFunction): string =>
  `${actionTypeDisplayName(run.actionType, t)} #${run.id}`;

export const actionTargetLabel = (target: ActionTargetRun, t?: TFunction): string => {
  if (target.rtuName) {
    return target.rtuId !== undefined ? `${target.rtuName} (${target.rtuId})` : target.rtuName;
  }

  return target.rtuId !== undefined
    ? String(t
      ? t('actionsWorkbench.rtuTarget', { defaultValue: 'RTU {{rtuId}}', rtuId: target.rtuId })
      : i18n.t('actionsWorkbench.rtuTarget', { defaultValue: 'RTU {{rtuId}}', rtuId: target.rtuId }))
    : target.ip || translate(t, 'actionsWorkbench.unknownRtu', 'Unknown RTU');
};

export const actionPhaseDisplayName = (
  phaseLabel: string | undefined,
  phaseCode: string | undefined,
  t: TFunction
): string => phaseLabel || (phaseCode ? formatActionPhaseLabel(phaseCode, t) : t('actionsWorkbench.notReported', { defaultValue: 'Not reported' }));

const UPLOAD_TRANSFER_PHASES = new Set([
  'UPLOAD_CONFIG',
  'UPLOAD_DEPLOY',
  'UPLOAD_LICENSE',
]);

export const getCancelPresentation = (hints?: ActionRunCommandHints, t?: TFunction, phase?: string) => {
  const scope = hints?.cancelScope ?? 'NONE';
  const enabled = Boolean(hints?.cancellable && scope !== 'NONE' && hints.scopeRevision);
  const uploadInProgress = !enabled && UPLOAD_TRANSFER_PHASES.has(normalizeToken(phase));
  return {
    enabled,
    scope,
    label: scope === 'ENTIRE_ACTION'
      ? translate(t, 'actionsWorkbench.cancel.action', 'Cancel action')
      : scope === 'ACCESSIBLE_TARGETS'
        ? translate(t, 'actionsWorkbench.cancel.accessibleTargets', 'Cancel accessible targets')
        : translate(t, 'actionsWorkbench.cancel.unavailable', 'Cancel unavailable'),
    reason: (uploadInProgress
      ? translate(t, 'actionsWorkbench.cancel.transferStartedReason', 'Cancellation is unavailable during this upload stage.')
      : undefined)
      || hints?.reason
      || hints?.cancelUnavailableReason
      || translate(t, 'actionsWorkbench.cancel.unavailableReason', 'Cancellation is not available for the current action state.'),
  };
};

export const formatActionDateTime = (value?: string, missing?: string): string => {
  if (!value) return missing ?? translate(undefined, 'actionsWorkbench.notStarted', 'Not started');
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleString(i18n.resolvedLanguage || i18n.language || undefined);
};

export const actionInitiatorIp = (
  action?: Pick<ActionRunSummary, 'initiatorIp' | 'initiatingUserIp'> | null
): string | undefined => action?.initiatorIp || action?.initiatingUserIp;

/**
 * Human-readable description of a Download Configuration/Deployment launch's
 * recorded selection ("Current configuration", "Latest deployment",
 * "Existing configuration — <filename>"), or null when the action carries no
 * selection - either because it isn't a download action, or because it
 * predates this field. Callers must render nothing (not a guess) for null.
 */
export const describeDownloadSelection = (
  actionType: string | undefined,
  selection: ActionDownloadSelection | undefined,
  t: TFunction
): string | null => {
  if (!selection?.selection) return null;
  const normalizedType = normalizeToken(actionType);
  const isDeployment = normalizedType === 'DOWNLOAD_DEPLOY';
  if (!isDeployment && normalizedType !== 'DOWNLOAD_CONFIG') return null;
  const value = selection.selection.toUpperCase();
  if (value === 'CURRENT') {
    return translate(t, 'actionsWorkbench.downloadSelection.current', 'Current configuration');
  }
  if (value === 'LATEST') {
    return translate(t, 'actionsWorkbench.downloadSelection.latest', 'Latest deployment');
  }
  if (value === 'EXISTING') {
    const kind = isDeployment
      ? translate(t, 'actionsWorkbench.downloadSelection.existingDeployment', 'Existing deployment')
      : translate(t, 'actionsWorkbench.downloadSelection.existingConfig', 'Existing configuration');
    return selection.filename ? `${kind} — ${selection.filename}` : kind;
  }
  return selection.selection;
};

export const formatActionFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null) {
    return translate(undefined, 'actionsWorkbench.unknownSize', 'Unknown size');
  }
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
